import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '../hooks/useAuth.ts'
import {
  INVITE_SHARE_TEXT_TEMPLATE,
  INVITE_UI,
  type InviteCreateResponse,
  type InviteShareMethod,
  type InviteShareResult,
} from '../config/invite.ts'
import {
  generateFriendInvite,
  shareInvite,
} from '../services/social.ts'

interface Props {
  open: boolean
  onClose: () => void
  /**
   * 未認証ユーザーがログイン動線を要求したときに呼ばれる。
   * 未指定なら単にモーダルを閉じる。
   */
  onRequireLogin?: () => void
}

type GenerateState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; invite: InviteCreateResponse }
  | { status: 'error'; message: string }

type ShareStatus =
  | { kind: 'idle' }
  | { kind: 'sharing' }
  | { kind: 'success'; method: InviteShareMethod }
  | { kind: 'cancelled' }
  | { kind: 'error'; message: string }

/**
 * フレンド招待モーダル（T16）。
 *
 * 役割:
 *   - `generateFriendInvite()` で招待コード / URL を取得
 *   - `shareInvite()` で LIFF / Web Share API / Capacitor Share / clipboard に順にフォールバック
 *   - コード・URL の表示とクリップボードコピー
 *
 * 未認証時は CTA を「ログインしてから…」に切り替え、onRequireLogin を通じて
 * 呼び出し側のログインモーダルを開かせる。
 *
 * スタイルは FriendMatchModal / AuthLoginModal と揃えて `auth-modal__*` BEM を使う。
 */
export function InviteFriendsModal({ open, onClose, onRequireLogin }: Props) {
  const { isSignedIn } = useAuth()
  const [genState, setGenState] = useState<GenerateState>({ status: 'idle' })
  const [shareStatus, setShareStatus] = useState<ShareStatus>({ kind: 'idle' })
  const [copied, setCopied] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  // 背景スクロール抑止
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  // ESC で閉じる（処理中は無効）
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (genState.status === 'loading' || shareStatus.kind === 'sharing') return
      onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, genState.status, shareStatus.kind, onClose])

  // 閉じたらローカル状態リセット
  useEffect(() => {
    if (open) return
    setGenState({ status: 'idle' })
    setShareStatus({ kind: 'idle' })
    setCopied(false)
    if (abortRef.current) {
      abortRef.current.abort()
      abortRef.current = null
    }
  }, [open])

  const doGenerate = useCallback(async () => {
    if (!isSignedIn) return
    const controller = new AbortController()
    if (abortRef.current) abortRef.current.abort()
    abortRef.current = controller
    setGenState({ status: 'loading' })
    setShareStatus({ kind: 'idle' })
    setCopied(false)
    try {
      const invite = await generateFriendInvite()
      // キャンセルされていれば無視
      if (controller.signal.aborted) return
      setGenState({ status: 'ready', invite })
    } catch (err) {
      if (controller.signal.aborted) return
      console.warn('[InviteFriendsModal] generateFriendInvite failed:', err)
      const message =
        err instanceof Error && err.message
          ? err.message
          : INVITE_UI.errorGenerate
      setGenState({ status: 'error', message })
    }
  }, [isSignedIn])

  // open かつ認証済みなら自動で招待リンク生成
  useEffect(() => {
    if (!open) return
    if (!isSignedIn) return
    if (genState.status !== 'idle') return
    void doGenerate()
  }, [open, isSignedIn, genState.status, doGenerate])

  const handleRetryGenerate = () => {
    void doGenerate()
  }

  const handleShare = async () => {
    if (genState.status !== 'ready') return
    setShareStatus({ kind: 'sharing' })
    const shareText = INVITE_SHARE_TEXT_TEMPLATE.replace(
      '{url}',
      genState.invite.url,
    )
    let result: InviteShareResult
    try {
      result = await shareInvite(genState.invite.url, shareText)
    } catch (err) {
      console.warn('[InviteFriendsModal] shareInvite failed:', err)
      setShareStatus({
        kind: 'error',
        message: err instanceof Error ? err.message : INVITE_UI.errorShare,
      })
      return
    }
    if (result.success) {
      setShareStatus({ kind: 'success', method: result.method })
    } else if (result.error === 'cancelled') {
      setShareStatus({ kind: 'cancelled' })
    } else {
      setShareStatus({
        kind: 'error',
        message: result.error ?? INVITE_UI.errorShare,
      })
    }
  }

  const handleCopy = async () => {
    if (genState.status !== 'ready') return
    const url = genState.invite.url
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url)
      } else {
        // fallback:ok — clipboard 非対応環境では textarea hack にフォールバック。
        // ここはフレンド機能の本流ではないため、失敗しても機能全体を止めない。
        const ta = document.createElement('textarea')
        ta.value = url
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
      }
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch (err) {
      console.warn('[InviteFriendsModal] copy failed:', err)
      setShareStatus({
        kind: 'error',
        message: INVITE_UI.errorShare,
      })
    }
  }

  const handleLoginClick = () => {
    if (onRequireLogin) {
      onRequireLogin()
    } else {
      onClose()
    }
  }

  if (!open) return null

  const shareLabel =
    genState.status === 'loading'
      ? INVITE_UI.shareButtonBusy
      : INVITE_UI.shareButton

  const canShare =
    isSignedIn &&
    genState.status === 'ready' &&
    shareStatus.kind !== 'sharing'

  return (
    <div
      className="auth-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-modal-title"
      onClick={() => {
        if (genState.status === 'loading') return
        if (shareStatus.kind === 'sharing') return
        onClose()
      }}
    >
      <div
        className="auth-modal__dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="auth-modal__header">
          <h2 id="invite-modal-title" className="auth-modal__title">
            {INVITE_UI.modalTitle}
          </h2>
          <button
            type="button"
            className="auth-modal__close"
            aria-label="閉じる"
            disabled={genState.status === 'loading' || shareStatus.kind === 'sharing'}
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <div className="auth-modal__body">
          <p className="auth-modal__reason">
            {isSignedIn
              ? INVITE_UI.description
              : INVITE_UI.descriptionSignedOut}
          </p>

          {/* 認証済み: 招待リンク生成・共有 */}
          {isSignedIn && (
            <>
              {genState.status === 'loading' && (
                <p
                  style={{
                    textAlign: 'center',
                    color: 'rgba(255,255,255,0.7)',
                    fontSize: 13,
                  }}
                >
                  {INVITE_UI.generatingLabel}
                </p>
              )}

              {genState.status === 'error' && (
                <div className="auth-modal__error" role="alert">
                  <p>{INVITE_UI.errorGenerate}</p>
                  <p className="auth-modal__error-detail">{genState.message}</p>
                  <div className="auth-modal__error-actions">
                    <button
                      type="button"
                      className="btn btn--secondary"
                      onClick={handleRetryGenerate}
                    >
                      {INVITE_UI.retryButton}
                    </button>
                  </div>
                </div>
              )}

              {genState.status === 'ready' && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                  }}
                >
                  <section
                    style={{
                      padding: 12,
                      background: 'rgba(255,255,255,0.06)',
                      borderRadius: 12,
                    }}
                  >
                    <p
                      style={{
                        fontSize: 12,
                        color: 'rgba(255,255,255,0.65)',
                        margin: 0,
                      }}
                    >
                      {INVITE_UI.codeLabel}
                    </p>
                    <p
                      style={{
                        fontSize: 22,
                        fontWeight: 900,
                        color: '#ffe066',
                        letterSpacing: '0.08em',
                        margin: '4px 0 10px',
                        wordBreak: 'break-all',
                      }}
                    >
                      {genState.invite.code}
                    </p>
                    <p
                      style={{
                        fontSize: 12,
                        color: 'rgba(255,255,255,0.65)',
                        margin: 0,
                      }}
                    >
                      {INVITE_UI.urlLabel}
                    </p>
                    <p
                      style={{
                        fontSize: 12,
                        color: 'rgba(255,255,255,0.9)',
                        margin: '4px 0 0',
                        wordBreak: 'break-all',
                      }}
                    >
                      {genState.invite.url}
                    </p>
                  </section>

                  <button
                    type="button"
                    className="btn btn--primary btn--large"
                    disabled={!canShare}
                    onClick={() => {
                      void handleShare()
                    }}
                  >
                    {shareStatus.kind === 'sharing' ? '共有中…' : shareLabel}
                  </button>

                  <button
                    type="button"
                    className="btn btn--secondary"
                    disabled={shareStatus.kind === 'sharing'}
                    onClick={() => {
                      void handleCopy()
                    }}
                  >
                    {copied ? INVITE_UI.copiedLabel : INVITE_UI.copyButton}
                  </button>

                  {shareStatus.kind === 'success' && (
                    <p
                      role="status"
                      style={{
                        fontSize: 13,
                        color: '#9ef59e',
                        textAlign: 'center',
                        margin: 0,
                      }}
                    >
                      {statusLabelForMethod(shareStatus.method)}
                    </p>
                  )}

                  {shareStatus.kind === 'cancelled' && (
                    <p
                      role="status"
                      style={{
                        fontSize: 13,
                        color: 'rgba(255,255,255,0.75)',
                        textAlign: 'center',
                        margin: 0,
                      }}
                    >
                      {INVITE_UI.statusCancelled}
                    </p>
                  )}

                  {shareStatus.kind === 'error' && (
                    <div className="auth-modal__error" role="alert">
                      <p>{INVITE_UI.errorShare}</p>
                      <p className="auth-modal__error-detail">
                        {shareStatus.message}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* 未認証: ログインへの導線 */}
          {!isSignedIn && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                type="button"
                className="btn btn--primary btn--large"
                onClick={handleLoginClick}
              >
                {INVITE_UI.signedOutActionLabel}
              </button>
              <button
                type="button"
                className="btn btn--secondary"
                onClick={onClose}
              >
                {INVITE_UI.closeButton}
              </button>
            </div>
          )}

          {isSignedIn && genState.status === 'ready' && (
            <button
              type="button"
              className="btn btn--secondary"
              onClick={onClose}
            >
              {INVITE_UI.closeButton}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function statusLabelForMethod(method: InviteShareMethod): string {
  switch (method) {
    case 'liff':
      return INVITE_UI.statusSharedLine
    case 'web_share':
      return INVITE_UI.statusSharedWebShare
    case 'capacitor_share':
      return INVITE_UI.statusSharedCapacitor
    case 'clipboard':
      return INVITE_UI.statusCopiedClipboard
    default:
      return INVITE_UI.statusShared
  }
}
